import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { AlertTriangle, CreditCard, KeyRound, Loader2, Save, ShieldCheck, Store, WalletCards } from "lucide-react";
import MainLayout from "../components/CommonComponents/MainLayout";
import { fetchCustomers } from "../services/customerApi";
import {
  fetchPaymentMaintenance,
  fetchPaymentSettings,
  updatePaymentMaintenance,
  updatePaymentSettings,
  fetchPlatformBillingSettings,
  updatePlatformBillingSettings,
} from "../services/paymentSettingsApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { HITPAY_EWALLET_METHODS } from "../constant/paymentMethod";

const modeOptions = [
  { value: "sandbox", label: "Sandbox" },
  { value: "production", label: "Production" },
];

const currencyOptions = [
  { value: "PHP", label: "PHP - Philippine Peso" },
];

const collectionModeOptions = [
  { value: "live", label: "Live HitPay checkout" },
  { value: "manual", label: "Manual store collection" },
];

const initialForm = {
  mode: "sandbox",
  currency: "PHP",
  enabled_methods: HITPAY_EWALLET_METHODS.map((method) => method.key),
  enabled: true,
  collection_mode: "live",
  api_key: "",
  webhook_salt: "",
};

const defaultServiceChargeSettings = {
  gcash: { rate_percent: 2, minimum_amount: 0 },
  qrph: { rate_percent: 1, minimum_amount: 20 },
  card: { rate_percent: 0, minimum_amount: 0 },
};

const normalizeServiceChargeSettings = (settings = {}) => (
  ["gcash", "qrph", "card"].reduce((result, method) => {
    const rawRate = Number(settings?.[method]?.rate_percent ?? defaultServiceChargeSettings[method].rate_percent);
    const rawMinimum = Number(settings?.[method]?.minimum_amount ?? defaultServiceChargeSettings[method].minimum_amount);
    result[method] = {
      rate_percent: Number.isFinite(rawRate) ? Math.min(Math.max(rawRate, 0), 100) : defaultServiceChargeSettings[method].rate_percent,
      minimum_amount: Number.isFinite(rawMinimum) ? Math.max(rawMinimum, 0) : defaultServiceChargeSettings[method].minimum_amount,
    };
    return result;
  }, {})
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 50,
    borderRadius: 10,
    borderColor: state.isFocused ? "#0f172a" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(15, 23, 42, 0.08)" : "none",
    backgroundColor: "#f8fafc",
    ":hover": { borderColor: "#94a3b8" },
  }),
  menu: (base) => ({ ...base, zIndex: 30, borderRadius: 12, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0f172a" : state.isFocused ? "#f1f5f9" : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#0f172a",
  }),
};

export default function PaymentSettingsPage() {
  const [form, setForm] = useState(initialForm);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [hasApiKey, setHasApiKey] = useState(false);
  const [hasWebhookSalt, setHasWebhookSalt] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [maintenance, setMaintenance] = useState({
    maintenance_enabled: false,
    maintenance_message: "E-wallet payments are temporarily unavailable.",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [billingForm, setBillingForm] = useState({
    mode: "sandbox",
    currency: "PHP",
    enabled_methods: ["gcash", "qrph"],
    service_charge_settings: defaultServiceChargeSettings,
    enabled: false,
    api_key: "",
    webhook_salt: "",
  });
  const [billingSecrets, setBillingSecrets] = useState({ api: false, salt: false });
  const [savingBilling, setSavingBilling] = useState(false);

  const status = useMemo(() => {
    if (!selectedCustomerId) {
      return {
        className: "border-slate-200 bg-slate-50 text-slate-700",
        message: "Select a customer to manage their e-wallet payment credentials.",
      };
    }

    if (form.collection_mode === "manual") {
      return {
        className: "border-emerald-300 bg-emerald-50 text-emerald-900",
        message: `Manual online payment recording is enabled for ${form.enabled_methods.length} method${form.enabled_methods.length === 1 ? "" : "s"}. Cashiers must tag the last 4 reference digits.`,
      };
    }

    if (maintenance.maintenance_enabled) {
      return {
        className: "border-rose-300 bg-rose-50 text-rose-900",
        message: "Global maintenance override is active. E-wallet checkout is disabled for all customers.",
      };
    }

    if (!form.enabled) {
      return {
        className: "border-slate-200 bg-slate-50 text-slate-700",
        message: "E-wallet checkout is currently disabled.",
      };
    }

    if (!form.enabled_methods?.length) {
      return {
        className: "border-amber-300 bg-amber-50 text-amber-900",
        message: "Choose at least one e-wallet method for this customer.",
      };
    }

    if (!hasApiKey || !hasWebhookSalt) {
      return {
        className: "border-amber-300 bg-amber-50 text-amber-900",
        message: "HitPay credentials are incomplete. Save the API key and webhook salt before accepting e-wallet payments.",
      };
    }

    return {
      className: "border-emerald-300 bg-emerald-50 text-emerald-900",
      message: `E-wallet checkout is enabled in ${form.mode} mode for ${form.enabled_methods.length} method${form.enabled_methods.length === 1 ? "" : "s"}.`,
    };
  }, [form.collection_mode, form.enabled, form.enabled_methods, form.mode, hasApiKey, hasWebhookSalt, maintenance.maintenance_enabled, selectedCustomerId]);

  const customerOptions = useMemo(
    () => customers.map((customer) => ({
      value: customer.id,
      label: `${customer.name}${customer.subdomain ? ` (${customer.subdomain})` : ""}`,
    })),
    [customers]
  );

  const selectedCustomerOption = customerOptions.find((option) => String(option.value) === String(selectedCustomerId)) || null;

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetchCustomers();
      const activeCustomers = (Array.isArray(response.data) ? response.data : [])
        .filter((customer) => String(customer.status || "").toUpperCase() === "ACTIVE");
      setCustomers(activeCustomers);

      if (activeCustomers.length > 0) {
        setSelectedCustomerId(String(activeCustomers[0].id));
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
      showErrorToast(error.response?.data?.message || "Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenance = async () => {
    try {
      const response = await fetchPaymentMaintenance();
      setMaintenance({
        maintenance_enabled: Boolean(response.data?.maintenance_enabled),
        maintenance_message: response.data?.maintenance_message || "E-wallet payments are temporarily unavailable.",
      });
    } catch (error) {
      console.error("Failed to load payment maintenance:", error);
      showErrorToast(error.response?.data?.message || "Failed to load payment maintenance.");
    }
  };

  const loadPlatformBilling = async () => {
    try {
      const response = await fetchPlatformBillingSettings();
      const config = response.data || {};
      setBillingForm({
        mode: config.mode || "sandbox",
        currency: config.currency || "PHP",
        enabled_methods: Array.isArray(config.enabled_methods) ? config.enabled_methods : ["gcash", "qrph"],
        service_charge_settings: normalizeServiceChargeSettings(config.service_charge_settings),
        enabled: Boolean(config.enabled),
        api_key: "",
        webhook_salt: "",
      });
      setBillingSecrets({ api: Boolean(config.has_api_key), salt: Boolean(config.has_webhook_salt) });
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load SaaS subscription billing.");
    }
  };

  const loadSettings = async (customerId) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const response = await fetchPaymentSettings(customerId);
      const config = response.data || {};
      setForm({
        mode: config.mode || "sandbox",
        currency: config.currency || "PHP",
        enabled_methods: Array.isArray(config.enabled_methods)
          ? config.enabled_methods.filter((method) => method !== "hitpay_checkout")
          : HITPAY_EWALLET_METHODS.map((method) => method.key),
        enabled: config.enabled ?? true,
        collection_mode: config.collection_mode || "live",
        api_key: "",
        webhook_salt: "",
      });
      setHasApiKey(Boolean(config.has_api_key));
      setHasWebhookSalt(Boolean(config.has_webhook_salt));
      setUpdatedAt(config.updatedAt || null);
    } catch (error) {
      console.error("Failed to load payment settings:", error);
      showErrorToast(error.response?.data?.message || "Failed to load payment settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    loadMaintenance();
    loadPlatformBilling();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadSettings(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await updatePaymentSettings({
        ...form,
        customer_id: selectedCustomerId,
      });
      const config = response.data?.config || {};
      setForm((prev) => ({
        ...prev,
        mode: config.mode || prev.mode,
        currency: config.currency || prev.currency,
        enabled_methods: Array.isArray(config.enabled_methods) ? config.enabled_methods : prev.enabled_methods,
        enabled: config.enabled ?? prev.enabled,
        collection_mode: config.collection_mode || prev.collection_mode,
        api_key: "",
        webhook_salt: "",
      }));
      setHasApiKey(Boolean(config.has_api_key));
      setHasWebhookSalt(Boolean(config.has_webhook_salt));
      setUpdatedAt(config.updatedAt || null);
      showSuccessToast("Payment settings saved.");
    } catch (error) {
      console.error("Failed to save payment settings:", error);
      showErrorToast(error.response?.data?.message || "Failed to save payment settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleMaintenanceToggle = async (enabled) => {
    setSavingMaintenance(true);
    try {
      const response = await updatePaymentMaintenance({
        maintenance_enabled: enabled,
        maintenance_message: maintenance.maintenance_message,
      });
      const nextMaintenance = response.data?.maintenance || {};
      setMaintenance({
        maintenance_enabled: Boolean(nextMaintenance.maintenance_enabled),
        maintenance_message: nextMaintenance.maintenance_message || maintenance.maintenance_message,
      });
      showSuccessToast(response.data?.message || "Payment maintenance updated.");
    } catch (error) {
      console.error("Failed to update payment maintenance:", error);
      showErrorToast(error.response?.data?.message || "Failed to update payment maintenance.");
    } finally {
      setSavingMaintenance(false);
    }
  };

  const handlePlatformBillingSubmit = async (event) => {
    event.preventDefault();
    setSavingBilling(true);
    try {
      const response = await updatePlatformBillingSettings(billingForm);
      const config = response.data?.config || {};
      setBillingForm((prev) => ({
        ...prev,
        mode: config.mode || prev.mode,
        currency: config.currency || prev.currency,
        enabled_methods: config.enabled_methods || prev.enabled_methods,
        service_charge_settings: normalizeServiceChargeSettings(config.service_charge_settings || prev.service_charge_settings),
        enabled: Boolean(config.enabled),
        api_key: "",
        webhook_salt: "",
      }));
      setBillingSecrets({ api: Boolean(config.has_api_key), salt: Boolean(config.has_webhook_salt) });
      showSuccessToast(response.data?.message || "SaaS subscription billing saved.");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to save SaaS subscription billing.");
    } finally {
      setSavingBilling(false);
    }
  };

  return (
    <MainLayout>
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col gap-7 px-4 py-8">
        <section>
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
            <WalletCards className="h-4 w-4" />
            Payment Gateway
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-slate-950">Payment Settings</h1>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
            Manage the HitPay credentials and e-wallet methods used by each customer. Secrets are write-only and stay masked after saving.
          </p>
        </section>

        <div className={`flex flex-col gap-2 rounded-lg border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between ${status.className}`}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <span>{status.message}</span>
          </div>
          {updatedAt && (
            <span className="text-xs font-semibold uppercase tracking-[0.16em] opacity-80">
              Updated {new Date(updatedAt).toLocaleString()}
            </span>
          )}
        </div>

        <section className="rounded-lg border border-amber-200 bg-amber-50/70 p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-950">Temporary E-Wallet Maintenance</h2>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-700">
                  Use this when HitPay or a wallet provider is under maintenance. It disables e-wallet checkout for every customer without changing their saved credentials.
                </p>
              </div>
            </div>
            <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${
              maintenance.maintenance_enabled ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
            }`}>
              {maintenance.maintenance_enabled ? "Active" : "Released"}
            </span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[1fr,auto]">
            <input
              value={maintenance.maintenance_message}
              onChange={(event) => setMaintenance((prev) => ({ ...prev, maintenance_message: event.target.value }))}
              className="w-full rounded-md border border-amber-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
              placeholder="Message shown when e-wallet payments are unavailable"
            />
            {maintenance.maintenance_enabled ? (
              <button
                type="button"
                onClick={() => handleMaintenanceToggle(false)}
                disabled={savingMaintenance}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-70"
              >
                {savingMaintenance ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Release for All
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleMaintenanceToggle(true)}
                disabled={savingMaintenance}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-rose-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-70"
              >
                {savingMaintenance ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                Apply to All Customers
              </button>
            )}
          </div>
        </section>

        <form onSubmit={handlePlatformBillingSubmit} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <fieldset disabled={savingBilling} className="grid gap-6 disabled:cursor-wait disabled:opacity-75">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-white">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-950">SaaS Subscription Billing</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
                    Configure ZeroOne's central HitPay account for one-time subscription payments and future recurring card charges. These credentials are separate from each customer's retail checkout account.
                  </p>
                </div>
              </div>
              <span className={`inline-flex w-fit rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] ${
                billingForm.enabled && billingSecrets.api && billingSecrets.salt
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}>
                {billingForm.enabled && billingSecrets.api && billingSecrets.salt ? "Ready" : "Setup required"}
              </span>
            </div>

            <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <span>
                <span className="block text-sm font-semibold text-slate-900">Enable online subscription payments</span>
                <span className="mt-1 block text-xs text-slate-500">Customer admins can pay bills using the approved methods below. Card auto-pay remains card-only.</span>
              </span>
              <input
                type="checkbox"
                checked={billingForm.enabled}
                onChange={(event) => setBillingForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                className="h-5 w-5 accent-slate-950"
              />
            </label>

            <div>
              <p className="text-sm font-semibold text-slate-900">Approved subscription methods</p>
              <p className="mt-1 text-xs text-slate-500">Only enabled methods appear in the admin billing modal. GCash and QR Ph require production mode.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {[
                  { key: "gcash", label: "GCash", description: "One-time e-wallet payment" },
                  { key: "qrph", label: "QR Ph", description: "One-time bank QR payment" },
                  { key: "card", label: "Card", description: "Manual payment and auto-pay" },
                ].map((method) => {
                  const checked = billingForm.enabled_methods.includes(method.key);
                  return (
                    <label key={method.key} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => setBillingForm((prev) => ({
                          ...prev,
                          enabled_methods: event.target.checked
                            ? [...prev.enabled_methods, method.key]
                            : prev.enabled_methods.filter((key) => key !== method.key),
                        }))}
                        className="mt-0.5 h-4 w-4 accent-slate-950"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{method.label}</span>
                        <span className="mt-1 block text-xs text-slate-500">{method.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">Subscription payment service charges</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                Added on top of the bill at checkout and shown separately in billing history. QR Ph uses whichever is higher: percentage or minimum fee.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {[
                  { key: "gcash", label: "GCash" },
                  { key: "qrph", label: "QR Ph" },
                  { key: "card", label: "Card" },
                ].map((method) => (
                  <label key={method.key} className="block rounded-lg border border-slate-200 bg-white p-3">
                    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{method.label}</span>
                    <div className="mt-2 flex items-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={billingForm.service_charge_settings?.[method.key]?.rate_percent ?? 0}
                        onChange={(event) => {
                          const rate = Math.min(Math.max(Number(event.target.value || 0), 0), 100);
                          setBillingForm((prev) => ({
                            ...prev,
                            service_charge_settings: {
                              ...normalizeServiceChargeSettings(prev.service_charge_settings),
                              [method.key]: {
                                ...normalizeServiceChargeSettings(prev.service_charge_settings)[method.key],
                                rate_percent: Number.isFinite(rate) ? rate : 0,
                              },
                            },
                          }));
                        }}
                        className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm font-semibold text-slate-900 outline-none"
                      />
                      <span className="border-l border-slate-200 px-3 py-2 text-sm font-semibold text-slate-500">%</span>
                    </div>
                    <span className="mt-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Minimum fee</span>
                    <div className="mt-1 flex items-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                      <span className="border-r border-slate-200 px-3 py-2 text-sm font-semibold text-slate-500">₱</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={billingForm.service_charge_settings?.[method.key]?.minimum_amount ?? 0}
                        onChange={(event) => {
                          const minimum = Math.max(Number(event.target.value || 0), 0);
                          setBillingForm((prev) => ({
                            ...prev,
                            service_charge_settings: {
                              ...normalizeServiceChargeSettings(prev.service_charge_settings),
                              [method.key]: {
                                ...normalizeServiceChargeSettings(prev.service_charge_settings)[method.key],
                                minimum_amount: Number.isFinite(minimum) ? minimum : 0,
                              },
                            },
                          }));
                        }}
                        className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm font-semibold text-slate-900 outline-none"
                      />
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">HitPay Mode</span>
                <Select
                  value={modeOptions.find((option) => option.value === billingForm.mode)}
                  onChange={(option) => setBillingForm((prev) => ({ ...prev, mode: option?.value || "sandbox" }))}
                  options={modeOptions}
                  styles={selectStyles}
                  isSearchable={false}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Currency</span>
                <Select
                  value={currencyOptions.find((option) => option.value === billingForm.currency) || currencyOptions[0]}
                  onChange={(option) => setBillingForm((prev) => ({ ...prev, currency: option?.value || "PHP" }))}
                  options={currencyOptions}
                  styles={selectStyles}
                  isSearchable={false}
                />
              </label>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Business API Key</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={billingForm.api_key}
                  onChange={(event) => setBillingForm((prev) => ({ ...prev, api_key: event.target.value }))}
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
                  placeholder={billingSecrets.api ? "Saved - enter a new key to replace" : "Enter HitPay API key"}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Webhook Salt</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={billingForm.webhook_salt}
                  onChange={(event) => setBillingForm((prev) => ({ ...prev, webhook_salt: event.target.value }))}
                  className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-4 focus:ring-slate-100"
                  placeholder={billingSecrets.salt ? "Saved - enter a new salt to replace" : "Enter HitPay webhook salt"}
                />
              </label>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-5 text-slate-500">
                Register <span className="font-mono text-slate-700">/api/subscription-billing/hitpay/webhook</span> in HitPay for charge created, method attached/detached, and subscription updated events.
              </p>
              <button
                type="submit"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70"
              >
                {savingBilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Subscription Billing
              </button>
            </div>
          </fieldset>
        </form>

        <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <fieldset disabled={loading || saving} className="grid gap-6 disabled:cursor-wait disabled:opacity-75">
            <div className="flex items-start gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-950 text-white">
                <CreditCard className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                  <h2 className="text-lg font-bold text-slate-950">Cashier Online Payments</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                  Choose whether this customer uses live HitPay checkout or records store-collected GCash, wallet, QR Ph, and card payments manually.
                  </p>
                </div>
              </div>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">Customer</span>
              <Select
                value={selectedCustomerOption}
                onChange={(option) => setSelectedCustomerId(option?.value ? String(option.value) : "")}
                options={customerOptions}
                styles={selectStyles}
                isSearchable
                placeholder="Select customer..."
              />
              <span className="mt-2 block text-xs text-slate-500">
                These credentials apply only to the selected customer.
              </span>
            </label>

            <label className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <span>
                <span className="block text-sm font-semibold text-slate-900">Show online payment methods in cashier</span>
                <span className="mt-1 block text-xs text-slate-500">Disable this if the customer only accepts cash from the POS.</span>
              </span>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))}
                className="h-5 w-5 accent-slate-950"
              />
            </label>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <span className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Store className="h-4 w-4 text-slate-500" />
                Cashier collection mode
              </span>
              <div className="grid gap-3 md:grid-cols-2">
                {collectionModeOptions.map((option) => {
                  const selected = form.collection_mode === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, collection_mode: option.value }))}
                      className={`rounded-lg border p-4 text-left transition ${selected ? "border-slate-950 bg-slate-50 shadow-sm" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                    >
                      <span className="block text-sm font-bold text-slate-950">{option.label}</span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">
                        {option.value === "live"
                          ? "Cashier opens a HitPay checkout and the order is finalized by webhook."
                          : "Cashier records the last 4 digits from the GCash/reference/card receipt and completes the sale immediately."}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">HitPay Mode</span>
                <Select
                  value={modeOptions.find((option) => option.value === form.mode)}
                  onChange={(option) => setForm((prev) => ({ ...prev, mode: option?.value || "sandbox" }))}
                  options={modeOptions}
                  styles={selectStyles}
                  isSearchable={false}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-900">Currency</span>
                <Select
                  value={currencyOptions.find((option) => option.value === form.currency) || currencyOptions[0]}
                  onChange={(option) => setForm((prev) => ({ ...prev, currency: option?.value || "PHP" }))}
                  options={currencyOptions}
                  styles={selectStyles}
                  isSearchable
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-900">Enabled Payment Methods</span>
              <Select
                value={HITPAY_EWALLET_METHODS.filter((option) => form.enabled_methods.includes(option.key))
                  .map((option) => ({
                    value: option.key,
                    label: form.collection_mode === "live" && form.mode === "sandbox" && option.sandboxSupported === false
                      ? `${option.label} (Production only)`
                      : option.label,
                  }))}
                onChange={(options) => setForm((prev) => ({
                  ...prev,
                  enabled_methods: (options || []).map((option) => option.value),
                }))}
                options={[
                  {
                    label: "E-Wallets",
                    options: HITPAY_EWALLET_METHODS.filter((option) => option.group === "wallet").map((option) => ({
                      value: option.key,
                      label: form.collection_mode === "live" && form.mode === "sandbox" && option.sandboxSupported === false
                        ? `${option.label} (Production only)`
                        : option.label,
                      isDisabled: form.collection_mode === "live" && form.mode === "sandbox" && option.sandboxSupported === false,
                    })),
                  },
                  {
                    label: "Bank / Card",
                    options: HITPAY_EWALLET_METHODS.filter((option) => option.group === "bank_card").map((option) => ({
                      value: option.key,
                      label: form.collection_mode === "live" && form.mode === "sandbox" && option.sandboxSupported === false
                        ? `${option.label} (Production only)`
                        : option.label,
                      isDisabled: form.collection_mode === "live" && form.mode === "sandbox" && option.sandboxSupported === false,
                    })),
                  },
                ]}
                styles={selectStyles}
                isMulti
                isSearchable
                closeMenuOnSelect={false}
                placeholder="Choose payment methods..."
              />
              <span className="mt-2 block text-xs text-slate-500">
                These methods appear in checkout for the selected customer only. Production-only limits apply to live HitPay checkout; manual recording can use the selected store-collected methods.
              </span>
            </label>

            <label className="block">
              <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <KeyRound className="h-4 w-4 text-slate-500" />
                API Key {hasApiKey ? "(leave blank to keep saved key)" : form.collection_mode === "manual" ? "(required only for live HitPay)" : ""}
              </span>
              <input
                name="api_key"
                type="password"
                value={form.api_key}
                onChange={(event) => setForm((prev) => ({ ...prev, api_key: event.target.value }))}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                autoComplete="new-password"
                placeholder={hasApiKey ? "Saved API key is hidden" : "Paste HitPay API key"}
                required={form.collection_mode === "live" && !hasApiKey}
              />
            </label>

            <label className="block">
              <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <KeyRound className="h-4 w-4 text-slate-500" />
                Webhook Salt {hasWebhookSalt ? "(leave blank to keep saved salt)" : form.collection_mode === "manual" ? "(required only for live HitPay)" : ""}
              </span>
              <input
                name="webhook_salt"
                type="password"
                value={form.webhook_salt}
                onChange={(event) => setForm((prev) => ({ ...prev, webhook_salt: event.target.value }))}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                autoComplete="new-password"
                placeholder={hasWebhookSalt ? "Saved webhook salt is hidden" : "Paste HitPay webhook salt"}
                required={form.collection_mode === "live" && !hasWebhookSalt}
              />
            </label>

            <button
              type="submit"
              disabled={saving || !selectedCustomerId}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving Payment Settings..." : "Save Payment Settings"}
            </button>
          </fieldset>
        </form>
      </div>
    </MainLayout>
  );
}
